import { useCallback, useEffect, useRef, useState } from "react";
import {
    Navigate,
    useLocation,
    useNavigate,
    useParams,
} from "react-router-dom";
import { ROUTES, buildRoute } from "../../routes/RouteNames";

import { categoryMap } from "../../config/CategoryMap";

import ButtonSubcategory from "../../components/ButtonSubcategory";
import Item from "../../components/Item";
import ItemSkeleton from "../../components/ItemSkeleton";
import LocationAutocomplete from "../../components/LocationAutocomplete";
import Path from "../../components/Path";
import RadiusSelect, { DEFAULT_RADIUS } from "../../components/RadiusSelect";
import { ASSETS } from "../../config/assets";
import makeSlug from "../../utils/makeSlug";

import "./Listing.css";

import { faStar as faStarEmpty } from "@fortawesome/free-regular-svg-icons";
import {
    faHandFist,
    faLightbulb,
    faMagnifyingGlass,
    faStar as faStarFull,
    faVideo,
    faWrench
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axiosInstance from "../../api/axiosInstance";

const Workers = () => {
    const { subcategory } = useParams();
    const [workers, setWorkers] = useState([]);
    const [loading, setLoading] = useState(false);
    const path = useLocation();
    const navigate = useNavigate();

    // Stav stránkování
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const isFetching = useRef(false);

    // Filtry
    const [searchTerm, setSearchTerm] = useState("");
    const [location, setLocation] = useState(null);
    const [radius, setRadius] = useState(DEFAULT_RADIUS);
    const [minRating, setMinRating] = useState(false); // Stav pro checkbox hodnocení
    const [networkOnly, setNetworkOnly] = useState(false); // Doporučeno mými uloženými
    const [verifiedOnly, setVerifiedOnly] = useState(false); // Pouze ověření uživatelé

    const [driverLicense, setDriverLicense] = useState("all");
    const [isOpenLicense, setIsOpenLicense] = useState(false);
    const selectRefLicense = useRef(null);

    const LICENSE_OPTIONS = [
        { value: "all", label: "Nerozhoduje" },
        { value: "none", label: "Bez požadavku" },
        { value: "B", label: "Skupina B" },
        { value: "C", label: "Skupina C" },
        { value: "D", label: "Skupina D" },
        { value: "E", label: "Skupina E" },
    ];

    // FUNKCE PRO NAČÍTNÍ DAT
    const fetchWorkers = useCallback(
        async (pageNumber, isInitial = false) => {
            if (isFetching.current && !isInitial) return;
            isFetching.current = true;

            try {
const response = await axiosInstance.get(
                    "/api/workers-listings",
                    {
                        params: {
                            subcategory,
                            location: location?.label || "",
                            lat: location?.latitude ?? "",
                            lng: location?.longitude ?? "",
                            radius: radius,
                            search: searchTerm,
                            minRating: minRating,
                            network_only: networkOnly,
                            verified_only: verifiedOnly,
                            ...(driverLicense !== "all" && { driver_license: driverLicense }),
                            page: pageNumber,
                        },
                    },
                );
                const newWorkers = response.data.data || [];
                setWorkers((prev) => {
                    if (isInitial) return newWorkers;
                    const existingIds = new Set(prev.map((w) => w.id));
                    const uniqueNewWorkers = newWorkers.filter(
                        (w) => !existingIds.has(w.id),
                    );
                    return [...prev, ...uniqueNewWorkers];
                });
                setHasMore(response.data.next_page_url !== null);
            } catch (error) {
                console.error("Chyba:", error);
            } finally {
                setLoading(false);
                isFetching.current = false;
            }
        },
        [subcategory, location, radius, searchTerm, minRating, networkOnly, verifiedOnly, driverLicense],
    );

    // Reset a první načtení při změně filtrů
    useEffect(() => {
        setPage(1);
        setHasMore(true);

        // KLÍČOV ZMĚNA: Okamžitě vymažeme staré výsledky, aby neproblikávaly se skeletony
        setWorkers([]);
        setLoading(true);

        const timeoutId = setTimeout(() => fetchWorkers(1, true), 300);
        return () => clearTimeout(timeoutId);
    }, [fetchWorkers]);

    // Načítání dalších stránek
    useEffect(() => {
        if (page > 1) fetchWorkers(page, false);
    }, [page, fetchWorkers]);

    // INFINITE SCROLL OBSERVER
    const observer = useRef();
    const lastWorkerRef = useCallback(
        (node) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMore) {
                        setPage((prev) => prev + 1);
                    }
                },
                { rootMargin: "0px 0px 200px 0px", threshold: 0.1 },
            );
            if (node) observer.current.observe(node);
        },
        [loading, hasMore],
    );

    useEffect(() => {
        const performReset = () => {
            setSearchTerm("");
            setLocation(null);
            setRadius(DEFAULT_RADIUS);
            setMinRating(false);
            setNetworkOnly(false);
            setVerifiedOnly(false);
            setDriverLicense("all");
            setIsOpenLicense(false);
            setPage(1);
            // fetchWorkers se spustí automaticky díky závislosti na searchTerm
        };

        window.addEventListener("reset-filters", performReset);
        return () => window.removeEventListener("reset-filters", performReset);
    }, []);

    // Zavření dropdownu řadiče průkazu při kliknutí mimo
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpenLicense && !selectRefLicense.current?.contains(event.target))
                setIsOpenLicense(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpenLicense]);

    // MEMOIZACE SEZNAMU (šetří výkon)
    // Memoizace handleItemClick pomocí useCallback
    const handleItemClick = useCallback(
        (id, firstName, lastName) => {
            const slug = makeSlug(`${firstName}-${lastName}`);
            const fullName = `${firstName} ${lastName}`; // Vytvoříme jméno
            navigate(`/user/${id}/${slug}`, {
                state: {
                    fromCategory: subcategory,
                    fromMode: "workers",
                    userName: fullName,
                },
            });
        },
        [navigate, subcategory],
    );

    // Definice povolených podkategorií
    const allowedSubcategories = [
        "light_technician",
        "sound_technician",
        "av_technician",
        "rigger",
        "stagehands",
    ];

    // Pokud subcategory není v povolených podkategotiích, přesměruj na 404
    if (subcategory && !allowedSubcategories.includes(subcategory)) {
        return <Navigate to={buildRoute(ROUTES.NOT_FOUND)} replace />;
    }

    // Pokud subcategory není v povolených podkategotiích, přesměruj na 404
    const titleHero = !subcategory
        ? categoryMap[path.pathname.split("/")[1]]
        : categoryMap[subcategory];

    return (
        <>
            <Path mode="workers" category={subcategory} />
            <h1 className="list_title">{titleHero}</h1>

            <div className="search_bar-container">
                <div className="search_bar-box">
                    <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        className="search_bar-icon"
                    />
                    <input
                        className="search_bar"
                        placeholder="Vyhledávejte"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    ></input>
                </div>
            </div>

            {!subcategory && (
                <div className="workers-nav">
                    {/* Tlačítka pro volbu podkategorií pracovníků */}
                    <ButtonSubcategory
                        icon={<FontAwesomeIcon icon={faLightbulb} />}
                        text="Osvětlovač"
                        onClick={() =>
                            navigate(
                                buildRoute(ROUTES.WORKERS_CATEGORY, {
                                    subcategory: "light_technician",
                                }),
                            )
                        }
                    />
                    <ButtonSubcategory
                        icon={
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="100"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="icon icon-tabler icons-tabler-filled icon-tabler-device-speaker"
                            >
                                <path
                                    stroke="none"
                                    d="M0 0h24v24H0z"
                                    fill="none"
                                />
                                <path d="M17 2a3 3 0 0 1 3 3v14a3 3 0 0 1 -3 3h-10a3 3 0 0 1 -3 -3v-14a3 3 0 0 1 3 -3zm-5 9a4 4 0 0 0 -3.995 3.8l-.005 .2a4 4 0 1 0 4 -4m0 -5a1 1 0 0 0 -1 1v.01a1 1 0 0 0 2 0v-.01a1 1 0 0 0 -1 -1" />
                            </svg>
                        }
                        text="Zvukař"
                        onClick={() =>
                            navigate(
                                buildRoute(ROUTES.WORKERS_CATEGORY, {
                                    subcategory: "sound_technician",
                                }),
                            )
                        }
                    />
                    <ButtonSubcategory
                        icon={<FontAwesomeIcon icon={faVideo} />}
                        text="AV Technik"
                        onClick={() =>
                            navigate(
                                buildRoute(ROUTES.WORKERS_CATEGORY, {
                                    subcategory: "av_technician",
                                }),
                            )
                        }
                    />
                    <ButtonSubcategory
                        icon={<FontAwesomeIcon icon={faWrench} />}
                        text="Rigger"
                        onClick={() =>
                            navigate(
                                buildRoute(ROUTES.WORKERS_CATEGORY, {
                                    subcategory: "rigger",
                                }),
                            )
                        }
                    />
                    <ButtonSubcategory
                        icon={<FontAwesomeIcon icon={faHandFist} />}
                        text="Stagehands"
                        onClick={() =>
                            navigate(
                                buildRoute(ROUTES.WORKERS_CATEGORY, {
                                    subcategory: "stagehands",
                                }),
                            )
                        }
                    />
                </div>
            )}
            <div className="list-container">
                <div className="all-filters">
                    <div className="filters">
                        <div className="location">
                            <label
                                htmlFor="filter_location"
                                className="body_base label-move"
                            >
                                Lokalita
                            </label>
                            <LocationAutocomplete
                                id="filter_location"
                                value={location}
                                onChange={setLocation}
                                placeholder="Celá ČR"
                            />
                            {location?.latitude && (
                                <RadiusSelect
                                    value={radius}
                                    onChange={setRadius}
                                />
                            )}
                        </div>
                        {/* Řidičský PRŮKAZ */}
                        <div className="location" ref={selectRefLicense}>
                            <div className="custom-select-wrapper">
                                <label
                                    htmlFor="driver_license"
                                    className="body_base label-move"
                                >
                                    Řidičský průkaz
                                </label>
                                <div
                                    className={`custom-select-down ${isOpenLicense ? "open" : ""}`}
                                    onClick={() => setIsOpenLicense(!isOpenLicense)}
                                >
                                    <span className="selected">
                                        {LICENSE_OPTIONS.find(
                                            (opt) =>
                                                opt.value === driverLicense,
                                        )?.label || "Nerozhoduje"}
                                    </span>
                                    <span
                                        className={`arrow ${isOpenLicense ? "rotate" : ""}`}
                                    >
                                        ▼
                                    </span>
                                </div>
                                {isOpenLicense && (
                                    <div className="options-down">
                                        {LICENSE_OPTIONS.map((opt) => (
                                            <div
                                                key={opt.value || "all"}
                                                className={`option ${driverLicense === opt.value ? "selected" : ""}`}
                                                onClick={() => {
                                                    setDriverLicense(opt.value);
                                                    setIsOpenLicense(false);
                                                }}
                                            >
                                                {opt.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <label
                            htmlFor="rating"
                            className="body_base label-move"
                        >
                            Hodnocení
                        </label>
                        <div
                            id="rating"
                            className="checkbox-container checkbox-container-filter"
                        >
                            <input
                                type="checkbox"
                                id="rating-checkbox"
                                className="custom-checkbox"
                                checked={minRating}
                                onChange={() => setMinRating(!minRating)}
                            />
                            <label htmlFor="rating-checkbox" className="checkbox-text">
                                <FontAwesomeIcon
                                    icon={faStarFull}
                                    className="star"
                                />
                                <FontAwesomeIcon
                                    icon={faStarFull}
                                    className="star"
                                />
                                <FontAwesomeIcon
                                    icon={faStarFull}
                                    className="star"
                                />
                                <FontAwesomeIcon
                                    icon={faStarFull}
                                    className="star"
                                />
                                <FontAwesomeIcon
                                    icon={faStarEmpty}
                                    className="star"
                                 />{" "}
                                a více
                            </label>
                        </div>

                        {/* KOMUNITA */}
                        <label
                            htmlFor="network_only"
                            className="body_base label-move"
                        >
                            Komunita
                        </label>
                        <div
                            id="network_only"
                            className="checkbox-container checkbox-container-filter"
                        >
                            <input
                                type="checkbox"
                                id="network_only-checkbox"
                                className="custom-checkbox"
                                checked={networkOnly}
                                onChange={() => setNetworkOnly(!networkOnly)}
                            />
                            <label htmlFor="network_only-checkbox" className="checkbox-text">
                                Doporučeno mými uloženými
                            </label>
                        </div>

                        <div
                            id="verified_only"
                            className="checkbox-container checkbox-container-filter"
                        >
                            <input
                                type="checkbox"
                                id="verified_only-checkbox"
                                className="custom-checkbox"
                                checked={verifiedOnly}
                                onChange={() => setVerifiedOnly(!verifiedOnly)}
                            />
                            <label htmlFor="verified_only-checkbox" className="checkbox-text">
                                Ověření pracovníci
                            </label>
                        </div>
                    </div>
                </div>
                <div className="all-items">
                    {!loading && (
                        <div className="results-count">{workers.length}</div>
                    )}
                    {/* Renderování seznamu */}
                    {workers.map((worker, index) => (
                        <Item
                            key={worker.id} // Zásadní: ID, ne index!
                            id={worker.id}
                            ref={
                                workers.length === index + 1
                                    ? lastWorkerRef
                                    : null
                            } // Ref přímo do Itemu
                            isFavouriteInitially={worker.is_favourite}
                            socialProofInitially={worker.social_proof}
                            isActiveWorker={worker.is_active_worker}
                            profile_picture={
                                worker.profile_image_url ||
                                ASSETS.default_avatar
                            }
                            rating={worker.review_value}
                            name={`${worker.first_name ?? ""} ${worker.last_name ?? ""}`}
                            role={worker.formatted_specs}
                            onClick={() =>
                                handleItemClick(
                                    worker.id,
                                    worker.first_name,
                                    worker.last_name,
                                )
                            }
                            isVerified={worker.is_verified}
                        />
                    ))}

                    {/* Načítací skeletony pod seznamem */}
                    {loading &&
                        Array.from({ length: 4 }).map((_, index) => (
                            <ItemSkeleton key={`skeleton-${index}`} />
                        ))}

                    {/* Prázdný stav */}
                    {!loading && workers.length === 0 && (
                        <div className="no-results_listing-container">
                            <h2 className="no-results_listing">
                                Nebyli nalezeni žádní pracovníci.
                            </h2>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Workers;
